import type { StorageLocation } from "@storytell/cp-client";
import { TbCheck, TbDownload, TbLoader2, TbMaximize, TbX } from "solid-icons/tb";
import { Show, createEffect, createSignal, onCleanup } from "solid-js";
import { Portal } from "solid-js/web";
import { ProcessingSpinner } from "~/components/ui";
import { useSignedImageURL } from "~/hooks/useSignedImageURL";
import { useWire } from "~/wire/WireProvider";

interface ImageWithSignedURLProps {
  storageLocation: StorageLocation;
  metadata?: {
    prompt?: string;
    revisedPrompt?: string;
    model?: string;
  };
}

export function ImageWithSignedURL(props: ImageWithSignedURLProps) {
  const wire = useWire();
  const [isHovered, setIsHovered] = createSignal(false);
  const [isModalOpen, setIsModalOpen] = createSignal(false);
  const [isDownloading, setIsDownloading] = createSignal(false);
  const [downloadSuccess, setDownloadSuccess] = createSignal(false);
  const [expandHovered, setExpandHovered] = createSignal(false);
  const [downloadHovered, setDownloadHovered] = createSignal(false);

  const storageLocation = () => props.storageLocation;
  const authToken = () => wire.auth.token();

  const { url, loading, error, refetch } = useSignedImageURL(storageLocation, () => ({
    authToken: authToken(),
  }));

  createEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen()) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen()) {
      document.addEventListener("keydown", handleEscKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    onCleanup(() => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "";
    });
  });

  const handleRetry = () => {
    refetch();
  };

  const handleDownload = async () => {
    const imageUrl = url();
    if (!imageUrl) return;

    setIsDownloading(true);

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);

      setIsDownloading(false);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 1500);
    } catch (err) {
      console.error("Failed to download image:", err);
      setIsDownloading(false);
    }
  };

  const altText = () => {
    const meta = props.metadata;
    if (!meta) return "Generated image";
    return meta.revisedPrompt || meta.prompt || "Generated image";
  };

  return (
    <div class="relative">
      <Show when={loading()}>
        <div class="flex items-center justify-center p-8 bg-inset rounded-lg">
          <ProcessingSpinner class="w-8 h-8" />
        </div>
      </Show>

      <Show when={error()}>
        <div class="flex flex-col items-center justify-center p-8 bg-inset rounded-lg text-sm text-secondary-text">
          <div class="text-center mb-4">
            <div class="mb-2">Failed to load image</div>
            <div class="text-xs text-tertiary-text">{error()?.message || "Unknown error"}</div>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            class="px-4 py-2 bg-primary-button text-primary-button-text rounded-lg hover:bg-primary-button-hover transition-colors"
          >
            Retry
          </button>
        </div>
      </Show>

      <Show when={!loading() && !error() && url()}>
        <div
          class="relative inline-block"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img src={url()!} alt={altText()} class="max-w-full h-auto rounded-lg" />

          <Show when={isHovered() || isDownloading() || downloadSuccess()}>
            <div class="absolute top-2 right-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                onMouseEnter={() => setExpandHovered(true)}
                onMouseLeave={() => setExpandHovered(false)}
                class="bg-black/70 hover:bg-black/90 text-white rounded-md transition-all duration-200 cursor-pointer"
                classList={{
                  "p-1.5": !expandHovered(),
                  "inline-flex items-center gap-1.5 px-2.5 py-1.5": expandHovered(),
                }}
                aria-label="Expand image"
              >
                <TbMaximize class="w-4 h-4" />
                <Show when={expandHovered()}>
                  <span class="text-xs whitespace-nowrap">Expand</span>
                </Show>
              </button>
              <button
                type="button"
                onClick={handleDownload}
                onMouseEnter={() => setDownloadHovered(true)}
                onMouseLeave={() => setDownloadHovered(false)}
                class="bg-black/70 hover:bg-black/90 text-white rounded-md transition-all duration-200 cursor-pointer"
                classList={{
                  "p-1.5": !downloadHovered() && !isDownloading() && !downloadSuccess(),
                  "inline-flex items-center gap-1.5 px-2.5 py-1.5":
                    downloadHovered() || isDownloading() || downloadSuccess(),
                }}
                disabled={isDownloading()}
                aria-label={downloadSuccess() ? "Downloaded!" : isDownloading() ? "Downloading..." : "Download image"}
              >
                <Show
                  when={isDownloading()}
                  fallback={
                    <Show when={downloadSuccess()} fallback={<TbDownload class="w-4 h-4" />}>
                      <TbCheck class="w-4 h-4" />
                    </Show>
                  }
                >
                  <TbLoader2 class="w-4 h-4 animate-spin" />
                </Show>
                <Show when={downloadHovered() || isDownloading() || downloadSuccess()}>
                  <span class="text-xs whitespace-nowrap">
                    {isDownloading() ? "Downloading..." : downloadSuccess() ? "Downloaded!" : "Download"}
                  </span>
                </Show>
              </button>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={isModalOpen()}>
        <Portal>
          <div
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              class="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <TbX class="w-6 h-6" />
            </button>

            <img
              src={url()!}
              alt={altText()}
              class="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </Portal>
      </Show>
    </div>
  );
}
